import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { SnWidget } from '@lib/api/entities';
import { SnOperationalViewsAPI } from '@lib/api/services';
import { SnFormFieldConfig } from '@lib/form';
import { t } from '@lib/translation';

@Component({
  selector: 'sn-add-widget-dialog',
  templateUrl: './add-widget-dialog.component.html',
  styleUrls: ['./add-widget-dialog.component.scss'],
})
export class AddWidgetDialogComponent implements OnInit, OnDestroy {
  public options = [
    {
      label: t('common.alerts'),
      value: {
        widget: 'alert',
        name: t('common.alerts'),
        row: null,
        col: null,
        width: 2,
        height: 4,
      },
    },
    {
      label: t('dashboard.operational.widgets.types.cameraLiveView'),
      value: {
        widget: 'camera_live_view',
        name: t('dashboard.operational.widgets.types.cameraLiveView'),
        width: 2,
        height: 4,
        col: null,
        row: null,
      },
    },
    {
      label: t('dashboard.operational.widgets.types.cameraGrids'),
      value: {
        widget: 'camera_grids',
        name: t('dashboard.operational.widgets.types.cameraGrids'),
        width: 2,
        height: 4,
        col: null,
        row: null,
      },
    },
    {
      label: t('common.chatChannel'),
      value: {
        widget: 'chat_channel',
        name: t('common.chatChannel'),
        width: null,
        height: null,
        col: null,
        row: null,
      },
    },
    {
      label: t('dashboard.operational.widgets.types.embedYoutube'),
      value: {
        widget: 'embed_youtube',
        name: t('dashboard.operational.widgets.types.embedYoutube'),
        width: null,
        height: null,
        col: null,
        row: null,
      },
    },
    {
      label: t('dashboard.operational.widgets.embedCode'),
      value: {
        widget: 'embed_code',
        name: t('dashboard.operational.widgets.embedCode'),
        width: null,
        height: null,
        col: null,
        row: null,
      },
    },
    {
      label: t('dashboard.operational.widgets.types.facebookLive'),
      value: {
        widget: 'facebook_live',
        name: t('dashboard.operational.widgets.types.facebookLive'),
        width: null,
        height: null,
        col: null,
        row: null,
      },
    },
    {
      label: t('dashboard.operational.widgets.types.content'),
      value: {
        widget: 'content',
        name: t('dashboard.operational.widgets.types.content'),
        width: null,
        height: null,
        col: null,
        row: null,
      },
    },
    {
      label: t('dashboard.operational.widgets.types.twitterTimelineEmbed'),
      value: {
        widget: 'twitter_timeline_embed',
        name: t('dashboard.operational.widgets.types.twitterTimelineEmbed'),
        width: 2,
        height: 3,
        col: null,
        row: null,
      },
    },
    {
      label: t('dashboard.operational.widgets.types.websiteIframe'),
      value: {
        widget: 'website_iframe',
        name: t('dashboard.operational.widgets.types.websiteIframe'),
        width: 2,
        height: 3,
        col: null,
        row: null,
      },
    },
    {
      label: t('dashboard.operational.widgets.types.mediaWidget'),
      value: {
        widget: 'media_widget',
        name: t('dashboard.operational.widgets.types.mediaWidget'),
        width: 2,
        height: 3,
        col: null,
        row: null,
      },
    },
  ];
  public isLoading: boolean = false;
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public formModel: any = {};
  public formFields: SnFormFieldConfig[] = [
    {
      key: 'widgets',
      type: 'select',
      templateOptions: {
        appearance: 'outline',
        floatLabel: 'always',
        placeholder: t('dashboard.operational.dialogs.selectWidget'),
        required: true,
        label: t('common.widget'),
        options: this.options,
        valueProp: 'value',
        labelProp: 'label',
        multiple: false,
      },
      hideExpression: () => {
        return this.data.widget?.widget === 'heatmap_analytics';
      },
    },
    {
      key: 'widgetName',
      type: 'input',
      templateOptions: {
        appearance: 'outline',
        floatLabel: 'always',
        placeholder: t('dashboard.operational.dialogs.nameYourWidget'),
        label: t('common.name'),
        valueProp: 'value',
        labelProp: 'label',
        multiple: false,
      },
      hideExpression: () => {
        return !this.form.value.widgets && this.data.widget?.widget !== 'heatmap_analytics';
      },
    },
    {
      key: 'hideTitle',
      type: 'checkbox',
      defaultValue: false,
      templateOptions: {
        label: t('dashboard.operational.dialogs.hideTitle'),
      },
      hideExpression: () => {
        return !this.form.value.widgets && this.data.widget?.widget !== 'heatmap_analytics';
      },
    },
  ];

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      viewId: string;
      widget: SnWidget;
    },
    private readonly _dialogRef: MatDialogRef<AddWidgetDialogComponent>,
    private readonly _operationalViewsAPI: SnOperationalViewsAPI,
  ) {}

  public ngOnInit(): void {
    if (this.data.widget?.id) {
      const widget = this.options.find((o) => o.value.widget === this.data.widget.widget);
      if (widget && this.data.widget.widget !== 'heatmap_analytics') {
        this.formModel = {
          widgets: widget.value,
          widgetName: this.data.widget.name,
          hideTitle: this.data.widget.meta.hideTitle,
        };
      }
      if (this.data.widget.widget === 'heatmap_analytics') {
        this.formModel = {
          widgetName: this.data.widget.name,
          hideTitle: this.data.widget.meta.hideTitle,
        };
      }
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
  public cancel(): void {
    this._dialogRef.close();
  }

  public submitForm(): void {
    this.isLoading = true;
    let payload;
    if (!this.data.widget || this.data.widget?.widget !== 'heatmap_analytics') {
      const name = this.form.value.widgetName;
      const formData = this.form.value.widgets;
      payload = {
        ...this.data.widget,
        name: name ? name : formData.name,
        widget: formData.widget,
        width: this.data.widget?.width || formData.width,
        height: this.data.widget?.height || formData.height,
        col: this.data.widget?.col || formData.col,
        row: this.data.widget?.row || formData.row,
        operationalView: this.data.viewId,
        meta: {
          ...this.data.widget?.meta,
          hideTitle: this.formModel.hideTitle,
        },
      };
    }
    if (this.data.widget && this.data.widget?.widget === 'heatmap_analytics') {
      const name = this.form.value.widgetName;
      const formData = 'heatmap_analytics';
      if (this.data.widget?.id) {
        if (this.data.widget.widget === 'heatmap_analytics') {
          this.data.widget.meta.layers.forEach((layer) => {
            switch (layer.mapOverlayStyle || layer.map_overlay_style) {
              case 'breadcrumbs':
                layer.layer_breadcrumb = null;
                break;
              case 'heatmap':
                layer.layer_heatmap = null;
                break;
            }
          });
        }
        payload = {
          ...this.data.widget,
          name: name ? name : t('dashboard.operational.widgets.types.heatmapAnalytics'),
          widget: formData,
          width: this.data.widget?.width,
          height: this.data.widget?.height,
          col: this.data.widget?.col,
          row: this.data.widget?.row,
          operationalView: this.data.viewId,
          meta: {
            ...this.data.widget?.meta,
            hideTitle: this.formModel.hideTitle,
          },
        };
      }
      this._operationalViewsAPI
        .updateViewWidget(this.data.viewId, this.data.widget.id, payload)
        .pipe(takeUntil(this._destroy$))
        .subscribe((widget) => {
          this.isLoading = false;
          this._dialogRef.close(widget);
        });
    }

    if (this.data.widget) {
      this._operationalViewsAPI
        .updateViewWidget(this.data.viewId, this.data.widget.id, payload)
        .pipe(takeUntil(this._destroy$))
        .subscribe((widget) => {
          this.isLoading = false;
          this._dialogRef.close(widget);
        });
    } else {
      this._operationalViewsAPI
        .createViewWidget(this.data.viewId, payload)
        .pipe(takeUntil(this._destroy$))
        .subscribe((widget) => {
          this.isLoading = false;
          this._dialogRef.close(widget);
        });
    }
  }
}
